import authService from 'ApiService/Auth/AuthService';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { RiEyeCloseLine, RiEyeLine } from 'react-icons/ri';
import ConfirmationModal from '../ConfirmationModal';

function Index() {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const validateField = (field, value) => {
    const errors = { ...fieldErrors };

    if (field === 'newPassword') {
      const passwordPattern =
        /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,15}$/;
      if (!passwordPattern.test(value)) {
        errors.newPassword =
          'Password must be 8-15 characters long, include at least one uppercase letter, one number, and one special character (!@#$%^&*).';
      } else {
        delete errors.newPassword;
      }
    }

    if (field === 'confirmPassword') {
      if (value !== formData.newPassword) {
        errors.confirmPassword = 'Passwords do not match.';
      } else {
        delete errors.confirmPassword;
      }
    }

    setFieldErrors(errors);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'newPassword' || name === 'confirmPassword') {
      validateField(name, value);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (event) => {
    if (loading) return;
    setLoading(true);

    try {
      const response = await authService.updatePassword(
        formData.oldPassword,
        formData.newPassword
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setFieldErrors({});
      } else {
        toast.error(response.data.message);
        setFormData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong.');
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } finally {
      setLoading(false);
      setIsModalOpen(false);
    }
  };

  const confirmCreation = (event) => {
    event.preventDefault();

    if (
      !formData.oldPassword ||
      !formData.newPassword ||
      !formData.confirmPassword
    ) {
      return toast.error('All fields are mandatory.');
    }

    if (Object.keys(fieldErrors).length > 0) {
      return toast.error('Please correct the errors before submitting.');
    }
    setIsModalOpen(true);
  };

  return (
    <Card extra="max-w-[405px] md:max-w-[510px] md:w-[510px] h-max mx-2.5 md:mx-auto mt-[50px] mb-auto py-2.5 px-4 md:!p-[50px] pt-8 md:pt-[50px]">
      <form onSubmit={confirmCreation}>
        <h3 className="mb-[10px] text-4xl font-bold text-gray-900 dark:text-white">
          Update Password
        </h3>
        <p className="mb-9 ml-1 text-base text-gray-600">
          Please enter and confirm your new password.
        </p>

        {/* Old Password */}
        <div className="relative mb-4">
          <InputField
            maxLength={25}
            minLength={8}
            label="Old Password"
            placeholder="Your Old Password"
            name="oldPassword"
            value={formData.oldPassword}
            onChange={handleChange}
            type={showPasswords.oldPassword ? 'text' : 'password'}
          />
          <button
            type="button"
            className="absolute right-4 top-2/3 -translate-y-1/2 transform text-lg text-navy-700 dark:text-white"
            onClick={() => togglePasswordVisibility('oldPassword')}
          >
            {showPasswords.oldPassword ? <RiEyeLine /> : <RiEyeCloseLine />}
          </button>
        </div>

        {/* New Password */}
        <div className="relative mb-2">
          <InputField
            maxLength={15}
            minLength={8}
            label="New Password"
            placeholder="Your New Password"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            type={showPasswords.newPassword ? 'text' : 'password'}
          />
          <button
            type="button"
            className="absolute right-4 top-2/3 -translate-y-1/2 transform text-lg text-navy-700 dark:text-white"
            onClick={() => togglePasswordVisibility('newPassword')}
          >
            {showPasswords.newPassword ? <RiEyeLine /> : <RiEyeCloseLine />}
          </button>
          {fieldErrors.newPassword && (
            <p className="mt-1 text-sm text-red-500">
              {fieldErrors.newPassword}
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="relative mb-2">
          <InputField
            maxLength={15}
            minLength={8}
            label="Confirm New Password"
            placeholder="Confirm New Password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            type={showPasswords.confirmPassword ? 'text' : 'password'}
          />
          <button
            type="button"
            className="absolute right-4 top-2/3 -translate-y-1/2 transform text-lg text-navy-700 dark:text-white"
            onClick={() => togglePasswordVisibility('confirmPassword')}
          >
            {showPasswords.confirmPassword ? <RiEyeLine /> : <RiEyeCloseLine />}
          </button>
          {fieldErrors.confirmPassword && (
            <p className="mt-1 text-sm text-red-500">
              {fieldErrors.confirmPassword}
            </p>
          )}
        </div>

        <button
          type="submit"
          className="mt-4 w-full rounded-xl bg-brand-500 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
        >
          Update Password
        </button>
      </form>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => {
          if (!loading) setIsModalOpen(false);
        }}
        onConfirm={handleSubmit}
        title="Confirm Password Update"
        message="Are you sure you want to update your password?"
        confirmText={loading ? 'Updating...' : 'Yes, Update'}
        cancelText="Cancel"
        icon="lock"
        disableConfirm={loading}
      />
    </Card>
  );
}

export default Index;
